import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    ListTable,
    LTHeader,
    LTItem,
    LTRow,
    LTMobileItem,
    LTPlaceholder,
} from 'components/common/ListTable';
import PrimaryButton from 'components/common/PrimaryButton/PrimaryButton';
import { AppDispatch, RootState } from 'redux/store';
import { getServiceProviders } from 'redux/service-providers/service-providers.middleware';
import { Application, ApplicationStatus } from 'api';

import DeadlineTimer from '../../../components/common/DeadlineTimer/DeadlineTimer';
import TextWithTooltip from './components/TextWithTooltip/TextWithTooltip';
import * as S from './VisaOffers.styled';

type VisaOffer = Pick<Application, 'program_name' | 'id' | 'status'> & {
    serviceProvider: string;
    updated_at: number;
    deadline: number;
};

const VisaOffers = () => {
    const { applications } = useSelector((state: RootState) => state.applications);
    const { providers } = useSelector((state: RootState) => state.serviceProviders);
    const [visaOffers, setVisaOffers] = useState<VisaOffer[]>([]);

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    useEffect(() => {
        const relevantVisaOffers = getRelevantVisaOffers(applications);
        const providersIDs = relevantVisaOffers.map((app) => app.service_provider_id.toString());

        dispatch(getServiceProviders(providersIDs));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applications]);

    useEffect(() => {
        if (providers.length > 0 && applications.length > 0) {
            const relevantVisaOffers = getRelevantVisaOffers(applications);
            const visaOffers = relevantVisaOffers.map<VisaOffer>((app) => {
                const provider = providers.find(
                    (provider) => provider.id === app.service_provider_id
                );
                return {
                    program_name: app.program_name ? app.program_name : 'Open Case',
                    updated_at: getInitialTime(app.last_status_updated_at),
                    deadline: getDeadline(app.last_status_updated_at, app.status),
                    id: app.id,
                    status: app.status,
                    serviceProvider: provider
                        ? provider.incorporated && provider.company_name
                            ? provider.company_name
                            : provider.super_admin_name
                        : '',
                };
            });

            setVisaOffers(visaOffers);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providers, applications]);

    const getRelevantVisaOffers = (apps: Application[]) => {
        return apps.filter(
            (app) =>
                app.status === ApplicationStatus.OFFER ||
                app.status === ApplicationStatus.COUNTEROFFER_CONSULTANT
        );
    };

    const getInitialTime = (date: string | null) => {
        if (date) {
            return new Date(date).getTime();
        }
        return 0;
    };

    const getDeadline = (initialDate: string | null, status: ApplicationStatus) => {
        const threeDays = 3 * 24 * 60 * 60 * 1000;
        const sixDays = 6 * 24 * 60 * 60 * 1000;
        const startTime = getInitialTime(initialDate);

        if (status === ApplicationStatus.OFFER) {
            return startTime + sixDays;
        }

        if (status === ApplicationStatus.COUNTEROFFER_CONSULTANT) {
            return startTime + threeDays;
        }

        return 0;
    };

    const isBtnDisabled = (vOffer: VisaOffer) => {
        const currentTime = new Date().getTime();
        if (vOffer.deadline - currentTime > 0) return false;
        return true;
    };

    return (
        <>
            <S.Title>visa offers</S.Title>

            <ListTable>
                <LTHeader>
                    <LTItem maxWidth="200px" minWidth="none">
                        <S.ColumnTitle>Service Provider</S.ColumnTitle>
                    </LTItem>
                    <LTItem maxWidth="320px" minWidth="none">
                        <S.ColumnTitle>Program Name</S.ColumnTitle>
                    </LTItem>
                    <LTItem maxWidth="136px">
                        <S.ColumnTitleCenter>Time Left</S.ColumnTitleCenter>
                    </LTItem>
                    <LTItem maxWidth="170px">{''}</LTItem>
                </LTHeader>
                {visaOffers.length === 0 && <LTPlaceholder>the list is empty</LTPlaceholder>}
                {visaOffers.map((visaOffer) => (
                    <LTRow key={visaOffer.id}>
                        <LTItem maxWidth="200px" minWidth="none">
                            <TextWithTooltip text={visaOffer.serviceProvider} />
                        </LTItem>
                        <LTItem maxWidth="320px" minWidth="none">
                            <TextWithTooltip
                                text={visaOffer.program_name ? visaOffer.program_name : 'Open Case'}
                            />
                        </LTItem>
                        <LTItem maxWidth="136px">
                            <S.TimerWrapper>
                                <DeadlineTimer deadline={visaOffer.deadline} />
                            </S.TimerWrapper>
                        </LTItem>
                        <LTItem maxWidth="170px" lastItem>
                            <PrimaryButton
                                style={{ width: '160px', height: '40px' }}
                                type="submit"
                                variant="contained"
                                size="min"
                                color="blue"
                                onClick={() => navigate(`/visa-process/${visaOffer.id}/agreement`)}
                                disabled={isBtnDisabled(visaOffer)}
                            >
                                {isBtnDisabled(visaOffer) ? 'Expired' : 'Act Now!'}
                            </PrimaryButton>
                        </LTItem>

                        {/*SMALL SCREEN VERSION*/}
                        <LTMobileItem name="Service Provider">
                            {visaOffer.serviceProvider}
                        </LTMobileItem>
                        <LTMobileItem name="Program Name">
                            {visaOffer.program_name ? visaOffer.program_name : 'Open Case'}
                        </LTMobileItem>
                        <LTMobileItem name="Time Left">
                            <DeadlineTimer deadline={visaOffer.deadline} />
                        </LTMobileItem>
                        <LTMobileItem name="">
                            <PrimaryButton
                                style={{ width: '160px', height: '40px' }}
                                type="submit"
                                variant="contained"
                                size="min"
                                color="blue"
                                onClick={() => navigate(`/visa-process/${visaOffer.id}/agreement`)}
                                disabled={isBtnDisabled(visaOffer)}
                            >
                                {isBtnDisabled(visaOffer) ? 'Expired' : 'Act Now!'}
                            </PrimaryButton>
                        </LTMobileItem>
                    </LTRow>
                ))}
            </ListTable>
        </>
    );
};

export default VisaOffers;
